import React, { useRef } from 'react';
import { useNavigate } from 'react-router';
import { CardTradeTile } from 'components/CardTradeTile';
import { WantListCardOverlay } from 'screens/Public/UserProfile/components/ProfileWantListTab/WantListCardOverlay';
import SendCardTooltip from 'screens/Main/tabs/Send/components/SendCardTooltip';
import { AssetUrls } from 'config/assets';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { formatRelativeTime } from 'utils/formatRelativeTime';
import { TradeAlert } from 'store/services/tradeAlerts/tradeAlertsApi';
import { TradeCard } from 'store/services/trade/tradeApi';
import './AlertCard.css';

// Inline SVG card-back placeholder — never depends on S3/CDN, can't 403
const CARD_BACK_DATA_URI = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='223' height='311' viewBox='0 0 223 311'%3E%3Crect width='223' height='311' rx='12' fill='%231a1a2e'/%3E%3Crect x='8' y='8' width='207' height='295' rx='8' fill='none' stroke='%236F51E4' stroke-width='2' opacity='0.4'/%3E%3Ctext x='111.5' y='165' text-anchor='middle' fill='%236F51E4' font-size='48' opacity='0.5'%3E%E2%9C%A6%3C/text%3E%3C/svg%3E";

// Build the ordered fallback chain (CDN size variations + scryfall + inline back).
const buildFallbacks = (alert: TradeAlert): string[] => {
  const fallbacks: string[] = [];
  const url = alert.card.imageUrl;
  if (url) {
    if (url.includes('/normal/')) {
      fallbacks.push(url.replace('/normal/', '/small/'));
      fallbacks.push(url.replace('/normal/', '/large/'));
    } else if (url.includes('/large/')) {
      fallbacks.push(url.replace('/large/', '/normal/'));
      fallbacks.push(url.replace('/large/', '/small/'));
    } else if (url.includes('/small/')) {
      fallbacks.push(url.replace('/small/', '/normal/'));
    }
  }
  if (alert.card.scryfallId) {
    fallbacks.push(AssetUrls.cards.getCardImage(alert.card.scryfallId, 'normal'));
    fallbacks.push(AssetUrls.cards.getCardImage(alert.card.scryfallId, 'small'));
  }
  return fallbacks;
};

interface AlertCardTileProps {
  alert: TradeAlert;
  /** Which tab the tile is being shown in — controls the dismiss vs restore icon. */
  variant: 'all' | 'hidden';
  onSend: (alert: TradeAlert) => void;
  onHide: (alert: TradeAlert) => void;
  onUnhide: (alert: TradeAlert) => void;
  /**
   * Requester identity block, rendered in the trader row above the image. Passed in
   * by-card mode; omitted in by-trader mode (the group renders it once in its header).
   */
  profile?: React.ReactNode;
  /** True when rendered inside an AlertTradeGroup — applies the lighter tile chrome. */
  grouped?: boolean;
}

/**
 * Adapter that maps a TradeAlert onto the shared CardTradeTile: the requester identity
 * goes in the trader row above the image, the card value sits centered below it, and the
 * alert-specific chrome (dismiss/restore + relative time overlay, the "username wants:"
 * accepted-printings flyout, the Want-List Add-to-Trade overlay) is injected through the
 * tile's slots. The image-fallback step machine stays here (useRef, not dataset).
 */
export const AlertCardTile: React.FC<AlertCardTileProps> = ({
  alert,
  variant,
  onSend,
  onHide,
  onUnhide,
  profile,
  grouped,
}) => {
  // Track the fallback step for this card's single image. useRef, NOT dataset —
  // React re-renders recreate the DOM element and would reset dataset values.
  const fallbackStepRef = useRef<number>(0);
  const fallbacksRef = useRef<string[]>(buildFallbacks(alert));
  const navigate = useNavigate();

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const step = fallbackStepRef.current;
    fallbackStepRef.current = step + 1;
    const fallbacks = fallbacksRef.current;
    if (step < fallbacks.length) {
      e.currentTarget.src = fallbacks[step];
      return;
    }
    e.currentTarget.src = CARD_BACK_DATA_URI;
  };

  const { card } = alert;

  // SendCardTooltip consumes a TradeCard — build a fully-typed one from the alert so the
  // accepted sets/finishes/languages/conditions render directly (no cast, no derivation).
  const tooltipCard: TradeCard = {
    id: alert.id,
    cardId: card.id,
    name: card.name,
    set: card.setCode,
    setCode: card.setCode,
    condition: card.condition,
    language: card.language,
    foil: card.foil,
    edition: '',
    imageUrl: card.imageUrl,
    marketPrice: 0,
    pointValue: alert.pointValue,
    quantity: 1,
    wantListId: alert.wantListItemId,
    scryfallId: card.scryfallId,
    acceptableSetDetails: card.acceptableSetDetails,
    acceptableConditions: card.acceptableConditions,
    acceptableLanguages: card.acceptableLanguages,
    acceptFoil: card.acceptFoil,
    acceptNonFoil: card.acceptNonFoil,
    availableInFoil: card.availableInFoil,
    availableInNonFoil: card.availableInNonFoil,
    availableLanguages: card.availableLanguages,
  };

  // Overlays anchored to the image: relative time (top-left, omitted when grouped) and
  // the dismiss/restore control (top-right, above the hover overlay).
  const imageOverlay = (
    <>
      {!grouped && (
        <span className="alert-card-time">{formatRelativeTime(alert.availableSince)}</span>
      )}
      {variant === 'all' ? (
        <button
          className="alert-card-dismiss"
          aria-label="Hide alert"
          title="Hide alert"
          onClick={() => onHide(alert)}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      ) : (
        <button
          className="alert-card-dismiss"
          aria-label="Restore alert"
          title="Restore alert"
          onClick={() => onUnhide(alert)}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M9 14L4 9l5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 9h11a5 5 0 0 1 0 10h-1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </>
  );

  return (
    <CardTradeTile
      card={{
        name: card.name,
        imageUrl: card.imageUrl,
        href: buildCardUrl(card),
        setCode: card.setCode,
        collectorNumber: card.collectorNumber,
        foil: card.foil,
      }}
      traderSlot={profile}
      hideTrader={grouped}
      cardValue={alert.pointValue}
      onSend={() => onSend(alert)}
      compact={grouped}
      chrome={grouped ? 'bare' : 'glass'}
      className={grouped ? 'alert-card alert-card--grouped' : 'alert-card'}
      imageContainerClassName="alert-card-image"
      imageOverlay={imageOverlay}
      onImageError={handleImageError}
      renderHover={({ rect, close }) => (
        <>
          <WantListCardOverlay
            cardName={card.name}
            quantity={1}
            addedCount={0}
            onViewCard={() => navigate(buildCardUrl(card))}
            onAddToTrade={() => onSend(alert)}
          />
          <SendCardTooltip
            cardData={tooltipCard}
            triggerRect={rect}
            username={alert.requester.username}
            onClose={close}
            onSetHover={() => {}}
            hoveredSetCode={null}
          />
        </>
      )}
    />
  );
};

export default AlertCardTile;
